import { useQuery } from "@tanstack/react-query";
import { fetchPackages } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { LIST_PAGES } from "@/config/pages";
import { IMAGES, DESTINATIONS, USP_STRIP_ITEMS } from "@/config/siteConfig";
import USPStrip from "@/components/USPStrip";
import PageHero from "@/components/PageHero";
import PackageCard from "@/components/PackageCard";
import SectionHeading from "@/components/SectionHeading";
import { HowItWorks, WhyUs } from "@/components/home/HomeSections";
import { FAQSection, FinalCTA } from "@/components/home/SocialSections";
import { Skeleton } from "@/components/ui/skeleton";
import NotFoundPage from "@/pages/NotFoundPage";

const PackagesPage = ({ pageKey }) => {
  const config = LIST_PAGES[pageKey];
  const { data: packages = [], isLoading } = useQuery({ queryKey: ["packages"], queryFn: fetchPackages });

  useSEO(config ? { title: config.seoTitle, description: config.seoDesc, path: `/${pageKey}` } : {});

  if (!config) return <NotFoundPage />;

  const filtered = packages.filter(config.filter);

  return (
    <main data-testid={`packages-page-${pageKey}`}>
      <PageHero image={IMAGES.pahalgamRiver} overline="Book Kashmir" title={config.title} subtitle={config.subtitle} />
      {["kashmir-tour-packages", "kashmir-honeymoon-packages", "kashmir-family-packages"].includes(pageKey) && (
        <USPStrip items={USP_STRIP_ITEMS} />
      )}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="Personalised For You" title={`${config.title} — Handpicked Holidays`}
            subtitle="Every package below can be customised: dates, hotels, pacing and experiences. Starting prices are per person — your final quote is tailored to your dates and group size." />
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-96 rounded-xl" />)}
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-slate-600">Packages coming soon — WhatsApp us and we'll plan this trip for you right away.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((pkg) => <PackageCard key={pkg.slug} pkg={pkg} />)}
            </div>
          )}
        </div>
      </section>
      {pageKey === "kashmir-tour-packages" && (
        <section className="pb-16 sm:pb-20" id="destinations">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading overline="Destinations" title="Explore Kashmir" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {DESTINATIONS.map((d) => (
                <a key={d.id} href={d.link} data-testid={`destination-card-${d.id}`} className="group">
                  <div className="overflow-hidden rounded-lg">
                    <img src={d.image} alt={`${d.name}, Kashmir`} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  </div>
                  <p className="mt-2 text-sm font-semibold text-[#0F2F24] group-hover:text-[#C28519] transition-colors">{d.name}</p>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
      <HowItWorks />
      <WhyUs />
      <FAQSection />
      <FinalCTA />
    </main>
  );
};

export default PackagesPage;
